import React, { useState, useEffect, useRef } from 'react';
import { Star, Heart, MapPin, Calendar, Users, Plane, Clock, BookOpen, Sparkles, ChevronRight, Globe, Sun, Moon, Prayer, ArrowRight, Home, Compass, ChevronLeft, Maximize2, X, Filter, Camera, Target, Zap, Cloud, Bookmark, Share2, ArrowDown, Music, Palette } from 'lucide-react';

const Destination = () => {
  const [selectedDestination, setSelectedDestination] = useState(0);
  const [likedDestinations, setLikedDestinations] = useState(new Set([0]));
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [gradientAngle, setGradientAngle] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);
  const [animationPhase, setAnimationPhase] = useState(0);
  const containerRef = useRef(null);

  const destinations = [
    {
      id: 0,
      name: "Mecca, Saudi Arabia",
      images: [
        "https://images.unsplash.com/photo-1580418827493-f2b22c0a76cb?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8bWVjY2ElMjBrYWFiYXxlbnwwfHwwfHx8MA%3D%3D",
        "https://images.unsplash.com/photo-1592326871020-04f58c1a52f3?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8bWVjY2ElMjBrYWFiYXxlbnwwfHwwfHx8MA%3D%3D",
        "https://images.unsplash.com/photo-1627728734379-a5f8c099763e?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NHx8bWVjY2ElMjBrYWFiYXxlbnwwfHwwfHx8MA%3D%3D",
        "https://images.unsplash.com/photo-1643134974035-696ba87f0c43?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTZ8fG1lY2NhJTIwa2FhYmF8ZW58MHx8MHx8fDA%3D"
      ],
      description: "The holiest city in Islam, home to the Kaaba at Al-Masjid al-Haram, the most sacred site for Muslims worldwide.",
      type: "Spiritual",
      distance: "Pilgrimage Destination",
      visitors: "20+ million annually",
      highlight: "Kaaba & Great Mosque",
      color: "from-amber-500 to-yellow-500",
      bgColor: "bg-gradient-to-br from-amber-500 to-yellow-500",
      season: "Year-round (Hajj in Dhu al-Hijjah)",
      bestFor: "Pilgrimage, Spiritual Journey"
    },
    {
      id: 1,
      name: "Switzerland",
      images: [
        "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80",
        "https://images.unsplash.com/photo-1530122037265-a5f1f91d3b99?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80",
        "https://images.unsplash.com/photo-1519681393784-d120267933ba?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80",
        "https://images.unsplash.com/photo-1501785888041-af3ef285b470?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80"
      ],
      description: "Breathtaking Alpine landscapes with pristine lakes, charming villages, and world-class ski resorts.",
      type: "Adventure & Luxury",
      distance: "4,200 km from Mecca",
      visitors: "12+ million annually",
      highlight: "Swiss Alps & Lakes",
      color: "from-blue-500 to-cyan-500",
      bgColor: "bg-gradient-to-br from-blue-500 to-cyan-500",
      season: "All seasons (Summer/Winter)",
      bestFor: "Skiing, Hiking, Luxury Travel"
    },
    {
      id: 2,
      name: "Medina, Saudi Arabia",
      images: [
        "https://images.unsplash.com/photo-1591604129939-f1efa4d9f7fa?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8bWVkaW5hJTIwc2F1ZGklMjBhcmFiaWF8ZW58MHx8MHx8fDA%3D",
        "https://images.unsplash.com/photo-1644585185180-7b51c7f302dd?q=80&w=715&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
      ],
      description: "The second holiest city, home to Al-Masjid an-Nabawi (The Prophet's Mosque) and burial place of Prophet Muhammad (PBUH).",
      type: "Historical & Spiritual",
      distance: "340 km from Mecca",
      visitors: "10+ million annually",
      highlight: "Prophet's Mosque",
      color: "from-green-500 to-emerald-500",
      bgColor: "bg-gradient-to-br from-green-500 to-emerald-500",
      season: "Year-round",
      bestFor: "Pilgrimage, Historical Tours"
    },
    {
      id: 3,
      name: "Jerusalem, Palestine",
      images: [
        "https://plus.unsplash.com/premium_photo-1697730130972-627b46998282?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MXx8amVydXNhbGVtfGVufDB8fDB8fHww",
        "https://images.unsplash.com/photo-1614517453351-6c1522fc7a56?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8N3x8amVydXNhbGVtfGVufDB8fDB8fHww"
      ],
      description: "Home to Al-Aqsa Mosque, the third holiest site in Islam, and a city of great significance to all Abrahamic religions.",
      type: "Historical",
      distance: "1,235 km from Mecca",
      visitors: "4+ million annually",
      highlight: "Al-Aqsa Mosque",
      color: "from-indigo-500 to-purple-500",
      bgColor: "bg-gradient-to-br from-indigo-500 to-purple-500",
      season: "Spring & Autumn",
      bestFor: "Religious Tourism, History"
    },
    {
      id: 4,
      name: "Istanbul, Turkey",
      images: [
        "https://images.unsplash.com/photo-1527838832700-5059252407fa?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8aW5zdGFuYnVsfGVufDB8fDB8fHww",
        "https://plus.unsplash.com/premium_photo-1691338312403-e9f7f7984eeb?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NXx8aW5zdGFuYnVsfGVufDB8fDB8fHww"
      ],
      description: "Home to Hagia Sophia and Blue Mosque, blending Islamic heritage with Byzantine history in a majestic setting.",
      type: "Cultural",
      distance: "2,150 km from Mecca",
      visitors: "15+ million annually",
      highlight: "Hagia Sophia",
      color: "from-purple-500 to-pink-500",
      bgColor: "bg-gradient-to-br from-purple-500 to-pink-500",
      season: "Spring & Autumn",
      bestFor: "Cultural Exploration, Shopping"
    },
    {
      id: 5,
      name: "Marrakech, Morocco",
      images: [
        "https://plus.unsplash.com/premium_photo-1699555731640-415f47a252d8?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NXx8bWFycmFrZWNofGVufDB8fDB8fHww",
        "https://images.unsplash.com/photo-1561642769-1bca263542e0?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8N3x8bWFycmFrZWNofGVufDB8fDB8fHww"
      ],
      description: "Known for its historic medina, bustling souks, and beautiful Islamic architecture and gardens.",
      type: "Cultural",
      distance: "4,890 km from Mecca",
      visitors: "3+ million annually",
      highlight: "Koutoubia Mosque",
      color: "from-orange-500 to-red-500",
      bgColor: "bg-gradient-to-br from-orange-500 to-red-500",
      season: "Spring & Autumn",
      bestFor: "Cultural Immersion, Shopping"
    }
  ];

  const meccaDetails = {
    population: "2.4 million",
    elevation: "277 m above sea level",
    prayerTimes: {
      fajr: "5:00 AM",
      dhuhr: "12:30 PM",
      asr: "3:45 PM",
      maghrib: "6:15 PM",
      isha: "7:45 PM"
    },
    importance: [
      "Birthplace of Prophet Muhammad (PBUH)",
      "Home to the Kaaba, built by Prophet Ibrahim (AS)",
      "Direction (Qibla) for Muslim prayers worldwide",
      "Annual Hajj pilgrimage destination",
      "Prohibited (Haram) area for non-Muslims"
    ],
    facts: [
      { icon: "🕋", title: "The Kaaba", detail: "Cuboid structure draped in black silk" },
      { icon: "🌙", title: "First Revelation", detail: "Where Quran was first revealed" },
      { icon: "🕌", title: "Grand Mosque", detail: "Largest mosque in the world" },
      { icon: "👣", title: "Tawaf", detail: "Circumambulation around Kaaba" }
    ]
  };

  const switzerlandDetails = {
    population: "8.7 million",
    elevation: "1,350 m average",
    attractions: {
      mountains: "Swiss Alps",
      lakes: "Lake Geneva, Lake Lucerne",
      cities: "Zurich, Geneva, Bern",
      activities: "Skiing, Hiking, Chocolate Tours"
    },
    highlights: [
      "Matterhorn - Iconic mountain peak",
      "Jungfrau Region - Top of Europe",
      "Swiss Chocolate & Cheese factories",
      "Efficient public transportation system",
      "Neutral country with rich history"
    ],
    facts: [
      { icon: "🏔️", title: "The Alps", detail: "Cover 60% of the country" },
      { icon: "⏰", title: "Punctuality", detail: "World-renowned for precision" },
      { icon: "🍫", title: "Chocolate", detail: "Produces 180,000 tons annually" },
      { icon: "🚂", title: "Trains", detail: "Most efficient rail network" }
    ]
  };

  // Generate floating elements
  const generateFloatingElements = (count, isMobile) => {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * (isMobile ? 20 : 30) + 10,
      delay: Math.random() * 5,
      duration: Math.random() * 20 + 15,
      type: i % 7,
      rotation: Math.random() * 360
    }));
  };

  const [floatingElements, setFloatingElements] = useState([]);

  // Mouse move effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 30 - 15,
        y: (e.clientY / window.innerHeight) * 30 - 15
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Scroll progress effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const heroHeight = containerRef.current?.clientHeight || 800;
      const progress = (scrollTop / heroHeight) * 100;
      setScrollProgress(Math.min(progress, 100));
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Animated gradient colors
  useEffect(() => {
    const interval = setInterval(() => {
      setGradientAngle((prev) => (prev + 0.5) % 360);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  // Animation phase for wave effect
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationPhase((prev) => (prev + 0.5) % 360);
    }, 30);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1500);
    setFloatingElements(generateFloatingElements(40, window.innerWidth < 768));
    return () => clearTimeout(timer);
  }, []);

  // Auto-slide images
  useEffect(() => {
    if (showImageModal) return;
    
    const interval = setInterval(() => {
      const currentDestination = destinations[selectedDestination];
      setCurrentImageIndex((prev) => 
        prev < currentDestination.images.length - 1 ? prev + 1 : 0
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [selectedDestination, showImageModal]);

  const toggleLike = (id) => {
    const newLiked = new Set(likedDestinations);
    if (likedDestinations.has(id)) {
      newLiked.delete(id);
    } else {
      newLiked.add(id);
    }
    setLikedDestinations(newLiked);
  };

  const nextImage = () => {
    const currentDestination = destinations[selectedDestination];
    setCurrentImageIndex((prev) => 
      prev < currentDestination.images.length - 1 ? prev + 1 : 0
    );
  };

  const prevImage = () => {
    const currentDestination = destinations[selectedDestination];
    setCurrentImageIndex((prev) => 
      prev > 0 ? prev - 1 : currentDestination.images.length - 1
    );
  };

  const openImageModal = () => {
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
  };

  const getCurrentDestinationDetails = () => {
    if (selectedDestination === 0) return meccaDetails;
    if (selectedDestination === 1) return switzerlandDetails;
    return meccaDetails;
  };

  return (
    <>
      <section 
        ref={containerRef}
        id="destinations" 
        className="min-h-screen relative overflow-hidden bg-gradient-to-br from-pink-50 via-rose-50 to-amber-50 py-16 px-4 sm:px-6 lg:px-8"
        style={{
          '--gradient-angle': `${gradientAngle}deg`,
          '--animation-phase': `${animationPhase}deg`,
          background: `radial-gradient(circle at ${mousePosition.x + 50}% ${mousePosition.y + 50}%, rgba(255, 182, 193, 0.3) 0%, transparent 60%),
                      radial-gradient(circle at ${100 - mousePosition.x + 50}% ${100 - mousePosition.y + 50}%, rgba(251, 207, 232, 0.35) 0%, transparent 60%),
                      linear-gradient(var(--gradient-angle), rgba(255, 240, 245, 0.95) 0%, rgba(255, 228, 230, 0.85) 50%, rgba(255, 240, 245, 0.95) 100%)`
        }}
      >
        {/* Dynamic background grid */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(rgba(255, 182, 193, 0.15) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(255, 182, 193, 0.15) 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
            maskImage: 'radial-gradient(circle at center, black 30%, transparent 70%)'
          }} />
        </div>

        {/* Animated wave background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-t from-amber-200/10 via-transparent to-transparent animate-wave"></div>
        </div>

        {/* Floating Bubbles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {floatingElements.filter(el => el.type === 0).map((bubble) => (
            <div
              key={`bubble-${bubble.id}`}
              className="absolute rounded-full animate-bubble-float"
              style={{
                left: `${bubble.x}%`,
                top: `${bubble.y}%`,
                width: `${bubble.size}px`,
                height: `${bubble.size}px`,
                background: `radial-gradient(circle at 30% 30%, 
                  rgba(255, 255, 255, 0.9) 0%, 
                  rgba(255, 255, 255, 0.7) 30%,
                  rgba(255, 200, 221, 0.4) 70%,
                  transparent 100%
                )`,
                border: '1px solid rgba(255, 255, 255, 0.5)',
                animationDelay: `${bubble.delay}s`,
                animationDuration: `${bubble.duration}s`,
                filter: 'blur(0.5px)',
                boxShadow: 'inset 0 0 15px rgba(255, 255, 255, 0.6), 0 0 25px rgba(255, 182, 193, 0.4)',
                willChange: 'transform, opacity'
              }}
            />
          ))}
        </div>

        {/* Floating Stars */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {floatingElements.filter(el => el.type === 1).map((star) => (
            <div
              key={`star-${star.id}`}
              className="absolute animate-star-twinkle"
              style={{
                left: `${star.x}%`,
                top: `${star.y}%`,
                width: `${star.size / 2}px`,
                height: `${star.size / 2}px`,
                background: `radial-gradient(circle, 
                  rgba(255, 255, 255, 0.95) 0%,
                  rgba(255, 255, 255, 0.8) 30%,
                  rgba(255, 182, 193, 0.6) 70%,
                  transparent 100%
                )`,
                clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
                animationDelay: `${star.delay}s`,
                animationDuration: `${star.duration}s`,
                filter: 'drop-shadow(0 0 3px rgba(255, 182, 193, 0.6))',
                willChange: 'transform, opacity, filter'
              }}
            />
          ))}
        </div>

        {/* Floating Hearts */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {floatingElements.filter(el => el.type === 3).map((heart) => (
            <div
              key={`heart-${heart.id}`}
              className="absolute animate-heart-float"
              style={{
                left: `${heart.x}%`,
                top: `${heart.y}%`,
                width: `${heart.size / 2}px`,
                height: `${heart.size / 2}px`,
                background: `linear-gradient(135deg, 
                  rgba(255, 100, 150, 0.3) 0%,
                  rgba(255, 150, 200, 0.2) 50%,
                  rgba(255, 100, 150, 0.1) 100%
                )`,
                clipPath: 'polygon(50% 0%, 100% 35%, 82% 100%, 50% 75%, 18% 100%, 0% 35%)',
                animationDelay: `${heart.delay}s`,
                animationDuration: `${heart.duration}s`,
                filter: 'blur(1px)',
                willChange: 'transform, opacity'
              }}
            />
          ))}
        </div>

        {/* Animated gradient orbs */}
        <div 
          className="absolute top-20 left-10 w-48 h-48 bg-gradient-to-br from-pink-400/25 via-rose-400/25 to-amber-400/15 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-orb-float hidden md:block"
          style={{
            transform: `translate(${mousePosition.x * 0.4}px, ${mousePosition.y * 0.4}px) rotate(${mousePosition.x * 0.5}deg)`,
            animationDelay: '0.5s'
          }}
        />
        
        <div 
          className="absolute bottom-32 right-10 w-56 h-56 bg-gradient-to-tr from-amber-300/30 via-pink-300/30 to-rose-300/20 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-orb-float-slow hidden md:block"
          style={{
            transform: `translate(${-mousePosition.x * 0.3}px, ${-mousePosition.y * 0.3}px) rotate(${-mousePosition.x * 0.3}deg)`,
            animationDelay: '1s'
          }}
        />

        {/* Animated decorative icons with trails */}
        <div className="hidden md:block">
          <div className="absolute top-20 right-20 group cursor-pointer">
            <div className="absolute inset-0 bg-pink-400/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
            <Sparkles 
              className="relative text-pink-400/60 w-10 h-10 animate-spin-slow group-hover:text-pink-500 group-hover:scale-110 transition-all duration-500"
              style={{ transform: `translate(${mousePosition.x * 0.1}px, ${mousePosition.y * 0.1}px)` }}
            />
          </div>
          
          <div className="absolute bottom-32 left-20 group cursor-pointer">
            <div className="absolute inset-0 bg-rose-400/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
            <Heart 
              className="relative text-rose-400/70 w-8 h-8 animate-pulse group-hover:text-rose-500 group-hover:scale-110 transition-all duration-500"
              style={{ transform: `translate(${mousePosition.x * 0.2}px, ${mousePosition.y * 0.2}px)` }}
            />
          </div>
          
          <div className="absolute top-32 left-1/3 group cursor-pointer">
            <div className="absolute inset-0 bg-amber-500/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
            <Star 
              className="relative text-amber-500/50 w-9 h-9 animate-spin group-hover:text-amber-600 group-hover:scale-110 transition-all duration-500"
              style={{ transform: `translate(${mousePosition.x * 0.15}px, ${mousePosition.y * 0.15}px)` }}
            />
          </div>
        </div>

        {/* Scroll progress indicator with glow effect */}
        <div className="fixed top-0 left-0 right-0 h-1 z-50">
          <div 
            className="h-full bg-gradient-to-r from-pink-500 via-rose-500 to-amber-500 transition-all duration-300 shadow-lg"
            style={{ width: `${scrollProgress}%` }}
          />
          <div 
            className="absolute top-0 h-full w-full bg-gradient-to-r from-pink-400/50 via-rose-400/50 to-amber-400/50 blur-lg transition-all duration-300"
            style={{ width: `${scrollProgress}%` }}
          />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          {/* Header Section */}
          <div className="text-center mb-12 sm:mb-16 relative">
            <div className="inline-flex items-center gap-2 sm:gap-3 mb-4 relative">
              {/* Animated border */}
              <div className="absolute -inset-2 sm:-inset-3 bg-gradient-to-r from-pink-400 via-rose-400 to-amber-400 animate-gradient-x rounded-full blur-lg opacity-30"></div>
              
              <div className="w-6 sm:w-8 md:w-12 h-px bg-gradient-to-r from-transparent via-pink-400 to-transparent"></div>
              <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-pink-500 animate-pulse relative" />
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 tracking-tight relative">
                <span className="bg-gradient-to-r from-pink-500 via-rose-500 to-amber-500 bg-clip-text text-transparent animate-gradient-flow">Dream</span> Destinations
                <span className="absolute -bottom-1 left-0 right-0 h-px bg-gradient-to-r from-transparent via-pink-400 to-transparent animate-width-pulse"></span>
              </h1>
              <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-pink-500 animate-pulse relative" />
              <div className="w-6 sm:w-8 md:w-12 h-px bg-gradient-to-l from-transparent via-pink-400 to-transparent"></div>
            </div>
            
            <p className="text-base sm:text-lg text-gray-600 max-w-xs sm:max-w-md md:max-w-3xl mx-auto mb-6 sm:mb-8 leading-relaxed animate-fade-in-stagger mt-6 relative px-4" style={{ animationDelay: '200ms' }}>
              <span className="relative inline-block">
                Explore breathtaking destinations from spiritual journeys to alpine adventures
                <div className="absolute -bottom-1 left-1/4 right-1/4 h-px bg-gradient-to-r from-transparent via-pink-300 to-transparent animate-width-pulse-slow"></div>
              </span>
            </p>
          </div>

          {/* Destination Selector with enhanced design */}
          <div className="mb-8 sm:mb-12 animate-fade-in-stagger" style={{ animationDelay: '300ms' }}>
            <div className="flex flex-wrap justify-center gap-2 sm:gap-3 relative">
              {destinations.map((destination, index) => (
                <button
                  key={destination.id}
                  onClick={() => {
                    setSelectedDestination(destination.id);
                    setCurrentImageIndex(0);
                  }}
                  className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full font-medium transition-all duration-500 transform hover:scale-105 animate-button-float group relative overflow-hidden ${
                    selectedDestination === destination.id
                      ? `${destination.bgColor} text-white shadow-lg`
                      : 'bg-white/80 backdrop-blur-sm text-gray-600 hover:bg-white hover:shadow-md'
                  }`}
                  style={{ animationDelay: `${index * 100 + 400}ms` }}
                >
                  {/* Hover effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                  
                  <div className="flex items-center gap-1 sm:gap-2 text-sm sm:text-base relative">
                    {selectedDestination === destination.id && <Filter className="w-3 h-3 sm:w-4 sm:h-4 animate-pulse" />}
                    <span>{destination.name.split(',')[0]}</span>
                    {selectedDestination === destination.id && (
                      <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 animate-bounce-right" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Main Destination Slider */}
          <div className="mb-12 sm:mb-20 animate-fade-in-stagger" style={{ animationDelay: '500ms' }}>
            <div className="bg-white/90 backdrop-blur-xl rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl border border-pink-100 hover:shadow-3xl transition-all duration-500 group">
              {/* Destination Hero with Image Slider */}
              <div className="relative h-[300px] sm:h-[400px] md:h-[500px] group/image-slider">
                {/* Main Image */}
                <img 
                  src={destinations[selectedDestination].images[currentImageIndex]}
                  alt={destinations[selectedDestination].name}
                  className="w-full h-full object-cover transition-transform duration-1000 group-hover/image-slider:scale-110"
                />
                
                {/* Glow effect on hover */}
                <div className="absolute inset-0 bg-gradient-to-r from-pink-400/0 via-rose-400/0 to-amber-400/0 group-hover/image-slider:from-pink-400/20 group-hover/image-slider:via-rose-400/20 group-hover/image-slider:to-amber-400/20 transition-all duration-700"></div>
                
                {/* Image Navigation */}
                <button 
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 p-2 sm:p-3 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-all duration-500 transform hover:scale-110 opacity-0 group-hover/image-slider:opacity-100 z-20 group/prev"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-pink-500/0 to-rose-500/0 group-hover/prev:from-pink-500/20 group-hover/prev:to-rose-500/20 transition-all duration-500"></div>
                  <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 text-gray-800 relative" />
                </button>
                
                <button 
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 p-2 sm:p-3 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-all duration-500 transform hover:scale-110 opacity-0 group-hover/image-slider:opacity-100 z-20 group/next"
                >
                  <div className="absolute inset-0 bg-gradient-to-l from-pink-500/0 to-rose-500/0 group-hover/next:from-pink-500/20 group-hover/next:to-rose-500/20 transition-all duration-500"></div>
                  <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 text-gray-800 relative" />
                </button>
                
                {/* Image Counter */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-1 bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full">
                  {destinations[selectedDestination].images.map((_, index) => (
                    <div 
                      key={index}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${index === currentImageIndex ? 'bg-white w-4' : 'bg-white/50'}`}
                    />
                  ))}
                </div>
                
                {/* Fullscreen Button */}
                <button 
                  onClick={openImageModal}
                  className="absolute top-4 right-4 p-2 sm:p-3 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-all duration-500 transform hover:scale-110 opacity-0 group-hover/image-slider:opacity-100 z-20 group/fullscreen"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-pink-500/0 to-rose-500/0 group-hover/fullscreen:from-pink-500/20 group-hover/fullscreen:to-rose-500/20 transition-all duration-500"></div>
                  <Maximize2 className="w-5 h-5 sm:w-6 sm:h-6 text-gray-800 relative" />
                </button>
                
                {/* Like Button */}
                <button 
                  onClick={() => toggleLike(selectedDestination)}
                  className="absolute top-4 left-4 p-2 sm:p-3 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-all duration-500 transform hover:scale-110 opacity-0 group-hover/image-slider:opacity-100 z-20 group/like"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-pink-500/0 to-rose-500/0 group-hover/like:from-pink-500/20 group-hover/like:to-rose-500/20 transition-all duration-500"></div>
                  <Heart 
                    className={`w-5 h-5 sm:w-6 sm:h-6 relative transition-all duration-500 ${
                      likedDestinations.has(selectedDestination) 
                        ? 'fill-rose-500 text-rose-500 scale-110 animate-heart-pop' 
                        : 'text-gray-600'
                    }`}
                  />
                </button>
                
                {/* Destination Info Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent">
                  <div className="absolute bottom-4 sm:bottom-6 md:bottom-8 left-4 sm:left-6 md:left-8 right-4 sm:right-6 md:right-8">
                    <div className="flex items-start sm:items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                          <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-amber-300" />
                          <span className={`px-3 sm:px-4 py-1 ${destinations[selectedDestination].bgColor} text-white rounded-full text-xs sm:text-sm font-medium`}>
                            {destinations[selectedDestination].type}
                          </span>
                          <span className="px-3 sm:px-4 py-1 bg-white/30 backdrop-blur-sm text-white rounded-full text-xs sm:text-sm font-medium">
                            {destinations[selectedDestination].season}
                          </span>
                        </div>
                        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2 sm:mb-3 transform translate-y-4 group-hover/image-slider:translate-y-0 transition-transform duration-700">
                          {destinations[selectedDestination].name}
                        </h2>
                        <p className="text-amber-100 text-sm sm:text-lg max-w-lg opacity-0 group-hover/image-slider:opacity-100 transition-opacity duration-700">
                          {destinations[selectedDestination].description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Destination Details Grid */}
              <div className="p-4 sm:p-6 md:p-8 lg:p-12">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-8 sm:mb-12">
                  {/* Quick Facts */}
                  <div className="space-y-4 sm:space-y-6 animate-fade-in-stagger" style={{ animationDelay: '600ms' }}>
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2 sm:gap-3">
                      <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-pink-500 animate-pulse-slow" />
                      Quick Facts
                    </h3>
                    <div className="space-y-3 sm:space-y-4">
                      <div className="flex items-center justify-between p-3 sm:p-4 bg-gradient-to-r from-pink-50 to-rose-50 rounded-xl hover:from-pink-100 hover:to-rose-100 transition-all duration-500 group/fact transform hover:-translate-y-1">
                        <span className="text-gray-600 text-sm sm:text-base">Best For</span>
                        <span className="font-bold text-gray-800 text-sm sm:text-base group-hover/fact:scale-105 transition-transform duration-500">
                          {destinations[selectedDestination].bestFor}
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-3 sm:p-4 bg-gradient-to-r from-pink-50 to-rose-50 rounded-xl hover:from-pink-100 hover:to-rose-100 transition-all duration-500 group/fact transform hover:-translate-y-1">
                        <span className="text-gray-600 text-sm sm:text-base">Annual Visitors</span>
                        <span className="font-bold text-gray-800 text-sm sm:text-base group-hover/fact:scale-105 transition-transform duration-500">
                          {destinations[selectedDestination].visitors}
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-3 sm:p-4 bg-gradient-to-r from-pink-50 to-rose-50 rounded-xl hover:from-pink-100 hover:to-rose-100 transition-all duration-500 group/fact transform hover:-translate-y-1">
                        <span className="text-gray-600 text-sm sm:text-base">Distance</span>
                        <span className="font-bold text-gray-800 text-sm sm:text-base group-hover/fact:scale-105 transition-transform duration-500">
                          {destinations[selectedDestination].distance}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Destination Specific Details */}
                  <div className="space-y-4 sm:space-y-6 animate-fade-in-stagger" style={{ animationDelay: '700ms' }}>
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2 sm:gap-3">
                      <Star className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500 animate-pulse" />
                      {selectedDestination === 0 ? "Prayer Times" : "Top Attractions"}
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 gap-2 sm:gap-3">
                      {selectedDestination === 0 ? (
                        Object.entries(meccaDetails.prayerTimes).map(([prayer, time]) => (
                          <div key={prayer} className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl p-3 sm:p-4 text-center hover:from-amber-100 hover:to-yellow-100 transition-all duration-500 transform hover:-translate-y-1 group/prayer">
                            <div className="font-semibold text-amber-700 capitalize text-xs sm:text-sm">{prayer}</div>
                            <div className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mt-1 group-hover/prayer:scale-110 transition-transform duration-500">{time}</div>
                          </div>
                        ))
                      ) : selectedDestination === 1 ? (
                        Object.entries(switzerlandDetails.attractions).map(([key, value]) => (
                          <div key={key} className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-3 sm:p-4 text-center hover:from-blue-100 hover:to-cyan-100 transition-all duration-500 transform hover:-translate-y-1 group/attraction">
                            <div className="font-semibold text-blue-700 capitalize text-xs sm:text-sm">
                              {key.replace(/([A-Z])/g, ' $1').trim()}
                            </div>
                            <div className="text-sm font-bold text-gray-800 mt-1 truncate group-hover/attraction:scale-105 transition-transform duration-500">{value}</div>
                          </div>
                        ))
                      ) : (
                        <div className="col-span-2 bg-gradient-to-r from-pink-50 to-rose-50 rounded-xl p-4 text-center hover:from-pink-100 hover:to-rose-100 transition-all duration-500 transform hover:-translate-y-1">
                          <div className="font-semibold text-pink-700">Explore details</div>
                          <div className="text-sm text-gray-600 mt-1">Discover more about this destination</div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Key Facts */}
                  <div className="space-y-4 sm:space-y-6 animate-fade-in-stagger" style={{ animationDelay: '800ms' }}>
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2 sm:gap-3">
                      <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-pink-500 animate-pulse" />
                      Key Facts
                    </h3>
                    <div className="grid grid-cols-2 gap-2 sm:gap-3">
                      {(selectedDestination === 0 ? meccaDetails.facts : 
                        selectedDestination === 1 ? switzerlandDetails.facts : meccaDetails.facts).map((fact, index) => (
                        <div key={index} className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-xl p-3 sm:p-4 text-center hover:from-pink-100 hover:to-rose-100 transition-all duration-500 transform hover:-translate-y-1 group/fact">
                          <div className="text-2xl sm:text-3xl mb-1 group-hover/fact:scale-125 transition-transform duration-500">{fact.icon}</div>
                          <div className="font-semibold text-pink-700 text-xs sm:text-sm">{fact.title}</div>
                          <div className="text-xs text-gray-600 mt-1">{fact.detail}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Destination Highlights */}
                <div className="animate-fade-in-stagger" style={{ animationDelay: '900ms' }}>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">
                    {selectedDestination === 0 ? "Spiritual Significance" : 
                     selectedDestination === 1 ? "Swiss Highlights" : "Destination Highlights"}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    {(selectedDestination === 0 ? meccaDetails.importance : 
                      selectedDestination === 1 ? switzerlandDetails.highlights : meccaDetails.importance).map((point, index) => (
                      <div 
                        key={index} 
                        className="flex items-start gap-3 p-3 sm:p-4 bg-gradient-to-r from-pink-50 to-rose-50 rounded-xl hover:from-pink-100 hover:to-rose-100 transition-all duration-500 group/highlight transform hover:-translate-y-2"
                        onMouseEnter={() => setHoveredCard(index)}
                        onMouseLeave={() => setHoveredCard(null)}
                      >
                        <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-all duration-500 ${
                          selectedDestination === 0 ? 'bg-gradient-to-r from-amber-500 to-yellow-500' : 
                          selectedDestination === 1 ? 'bg-gradient-to-r from-blue-500 to-cyan-500' : 'bg-gradient-to-r from-pink-500 to-rose-500'
                        } group-hover/highlight:scale-110 group-hover/highlight:rotate-12`}>
                          <span className="text-white font-bold text-sm">{index + 1}</span>
                        </div>
                        <p className="text-gray-700 flex-1 text-sm sm:text-base group-hover/highlight:translate-x-2 transition-transform duration-500">{point}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Other Destinations */}
          <div className="mb-12 sm:mb-16 animate-fade-in-stagger" style={{ animationDelay: '1000ms' }}>
            <div className="flex items-center justify-between mb-6 sm:mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">More Amazing Destinations</h2>
              <div className="flex items-center gap-2 text-pink-600 group">
                <Plane className="w-4 h-4 sm:w-5 sm:h-5 group-hover:rotate-12 transition-transform duration-500" />
                <span className="font-medium text-sm sm:text-base">Explore More</span>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {destinations.slice(0, 3).map((destination, index) => (
                <div 
                  key={destination.id}
                  className="group bg-white/90 backdrop-blur-xl rounded-xl sm:rounded-2xl overflow-hidden shadow-lg border border-pink-100 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 animate-card-float"
                  style={{ animationDelay: `${index * 100 + 1100}ms` }}
                  onMouseEnter={() => setHoveredCard(destination.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  {/* Destination Image */}
                  <div className="relative h-40 sm:h-48 overflow-hidden">
                    <img 
                      src={destination.images[0]}
                      alt={destination.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 group-hover:rotate-1"
                    />
                    
                    {/* Sparkle effect */}
                    {[...Array(3)].map((_, i) => (
                      <div
                        key={i}
                        className="absolute w-2 h-2 bg-white rounded-full animate-sparkle"
                        style={{
                          left: `${Math.random() * 80 + 10}%`,
                          top: `${Math.random() * 60 + 20}%`,
                          animationDelay: `${i * 0.3}s`
                        }}
                      />
                    ))}
                    
                    <div className="absolute top-3 sm:top-4 right-3 sm:right-4">
                      <button 
                        onClick={() => toggleLike(destination.id)}
                        className="p-1.5 sm:p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-all duration-500 transform hover:scale-110 group/like relative overflow-hidden"
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-pink-500/0 to-rose-500/0 group-hover/like:from-pink-500/20 group-hover/like:to-rose-500/20 transition-all duration-500"></div>
                        <Heart 
                          className={`w-4 h-4 sm:w-5 sm:h-5 relative transition-all duration-500 ${
                            likedDestinations.has(destination.id) 
                              ? 'fill-rose-500 text-rose-500 scale-110 animate-heart-pop' 
                              : 'text-gray-600'
                          }`}
                        />
                      </button>
                    </div>
                    <div className={`absolute bottom-3 sm:bottom-4 left-3 sm:left-4 px-2 sm:px-3 py-1 text-white text-xs sm:text-sm font-medium rounded-full ${destination.bgColor} group-hover:scale-110 transition-transform duration-500`}>
                      {destination.type}
                    </div>
                  </div>

                  {/* Destination Info */}
                  <div className="p-4 sm:p-6">
                    <div className="flex items-start justify-between mb-2 sm:mb-3">
                      <h3 className="text-lg sm:text-xl font-bold text-gray-800 group-hover:scale-105 transition-transform duration-500">{destination.name}</h3>
                      <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-pink-500" />
                    </div>
                    
                    <p className="text-gray-600 text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-2">
                      {destination.description}
                    </p>
                    
                    <div className="space-y-2 sm:space-y-3">
                      <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
                        <Compass className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span>Distance: {destination.distance}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
                        <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span>Visitors: {destination.visitors}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
                        <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span>Best Season: {destination.season}</span>
                      </div>
                      <div className="pt-2 sm:pt-3 border-t border-pink-100">
                        <span className="text-xs sm:text-sm font-medium text-pink-600 group-hover:scale-105 transition-transform duration-500 inline-block">
                          Highlight: {destination.highlight}
                        </span>
                      </div>
                    </div>
                    
                    <button 
                      onClick={() => {
                        setSelectedDestination(destination.id);
                        setCurrentImageIndex(0);
                        window.scrollTo({ top: containerRef.current.offsetTop, behavior: 'smooth' });
                      }}
                      className={`w-full mt-4 sm:mt-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-medium transition-all duration-500 transform hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-2 group/btn ${
                        selectedDestination === destination.id 
                          ? `${destination.bgColor} text-white shadow-lg`
                          : 'bg-gradient-to-r from-pink-50 to-rose-50 text-gray-700 hover:from-pink-100 hover:to-rose-100 hover:shadow-md'
                      }`}
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700"></div>
                      <span className="text-sm sm:text-base relative">
                        {selectedDestination === destination.id ? 'Currently Viewing' : 'View Details'}
                      </span>
                      {selectedDestination === destination.id && (
                        <ChevronRight className="w-4 h-4 animate-bounce-right relative" />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Stats with floating effect */}
          {/* <div className="max-w-4xl mx-auto mb-12 sm:mb-16 animate-fade-in-stagger" style={{ animationDelay: '1200ms' }}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 md:gap-6 px-4">
              {[
                { icon: Globe, value: destinations.length, label: 'Destinations', color: 'from-pink-500 via-rose-500 to-pink-500' },
                { icon: Heart, value: likedDestinations.size, label: 'Loved Places', color: 'from-rose-500 via-pink-500 to-rose-500' },
                { icon: Bookmark, value: '∞', label: 'Experiences', color: 'from-pink-400 via-rose-400 to-pink-400' },
                { icon: Sparkles, value: '6', label: 'Categories', color: 'from-rose-400 via-pink-400 to-rose-400' },
              ].map((stat, index) => (
                <div 
                  key={index}
                  className="bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-pink-100 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 group relative overflow-hidden animate-stat-float"
                  style={{ animationDelay: `${index * 100 + 1300}ms` }}
                >
                   Background glow 
                  <div className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-20 transition-opacity duration-500" 
                       style={{ background: `linear-gradient(to bottom right, ${stat.color.replace(/from-|via-|to-/g, '').split(' ')[0]}, ${stat.color.replace(/from-|via-|to-/g, '').split(' ')[2]})` }}></div>
                  
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center mb-2 sm:mb-4 group-hover:scale-110 transition-transform duration-500 relative overflow-hidden`}>
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                    <stat.icon className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white relative" />
                  </div>
                  <div className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-1 group-hover:scale-105 transition-transform duration-500">{stat.value}</div>
                  <div className="text-xs sm:text-sm text-gray-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div> */}

          {/* Journey Planning
          <div className="bg-gradient-to-r from-pink-500 via-rose-500 to-amber-500 rounded-xl sm:rounded-2xl md:rounded-3xl p-6 sm:p-8 md:p-12 text-white mb-12 sm:mb-16 animate-fade-in-stagger" style={{ animationDelay: '1400ms' }}>
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4">Plan Your Perfect Journey</h2>
              <p className="text-pink-100 mb-6 sm:mb-8 text-sm sm:text-lg">
                From spiritual pilgrimages to alpine adventures, we help you create unforgettable experiences
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 sm:p-6 hover:bg-white/30 transition-all duration-500 transform hover:-translate-y-1 group relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                  <div className="text-2xl sm:text-3xl mb-2 sm:mb-3 group-hover:scale-110 transition-transform duration-500">🕋</div>
                  <h3 className="font-bold text-lg sm:text-xl mb-2 group-hover:scale-105 transition-transform duration-500">Spiritual Journeys</h3>
                  <p className="text-pink-100 text-sm">Guides for pilgrimage rituals and spiritual tours</p>
                </div>
                
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 sm:p-6 hover:bg-white/30 transition-all duration-500 transform hover:-translate-y-1 group relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                  <div className="text-2xl sm:text-3xl mb-2 sm:mb-3 group-hover:scale-110 transition-transform duration-500">🏔️</div>
                  <h3 className="font-bold text-lg sm:text-xl mb-2 group-hover:scale-105 transition-transform duration-500">Alpine Adventures</h3>
                  <p className="text-pink-100 text-sm">Skiing, hiking, and luxury mountain experiences</p>
                </div>
                
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 sm:p-6 hover:bg-white/30 transition-all duration-500 transform hover:-translate-y-1 group relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                  <div className="text-2xl sm:text-3xl mb-2 sm:mb-3 group-hover:scale-110 transition-transform duration-500">🌍</div>
                  <h3 className="font-bold text-lg sm:text-xl mb-2 group-hover:scale-105 transition-transform duration-500">Cultural Tours</h3>
                  <p className="text-pink-100 text-sm">Explore historical sites and cultural heritage</p>
                </div>
              </div>
              
              <button className="px-6 sm:px-8 py-3 sm:py-4 bg-white text-pink-600 rounded-xl font-bold hover:bg-pink-50 transition-all duration-500 transform hover:scale-105 active:scale-95 flex items-center justify-center gap-2 mx-auto group relative overflow-hidden animate-button-pulse">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                <span className="text-sm sm:text-base relative">Start Planning Your Trip</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-500 relative" />
              </button>
            </div>
          </div> */}

          {/* Quote */}
          <div className="mt-8 sm:mt-12 text-center max-w-lg sm:max-w-3xl mx-auto animate-fade-in-stagger" style={{ animationDelay: '1500ms' }}>
            <div className="relative">
              <Star className="absolute -top-4 sm:-top-6 left-1/2 transform -translate-x-1/2 w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-pink-500 animate-spin-slow" />
              <blockquote className="text-lg sm:text-xl md:text-2xl italic text-gray-700 mb-4 p-6 sm:p-8 bg-white/50 backdrop-blur-sm rounded-2xl sm:rounded-3xl border border-pink-100 shadow-lg group hover:shadow-2xl transition-all duration-500">
                "Traveling – it leaves you speechless, then turns you into a storyteller."
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-pink-50/10 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-700 rounded-2xl sm:rounded-3xl"></div>
              </blockquote>
              <p className="text-gray-600 mt-4 text-sm sm:text-base">— Ibn Battuta, Great Muslim Explorer</p>
            </div>
          </div>
        </div>

        {/* Scroll indicator for mobile with enhanced animation */}
        <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 z-20 md:hidden">
          <div className="flex flex-col items-center group cursor-pointer animate-bounce-slow">
            <div className="relative">
              <div className="absolute -inset-3 bg-gradient-to-b from-pink-400/20 to-transparent rounded-full blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <ArrowDown className="relative w-6 h-6 text-pink-500/70 group-hover:text-pink-600 group-hover:scale-125 transition-all duration-500" />
            </div>
            <div className="text-xs text-pink-600/70 mt-2 tracking-wider font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              Scroll for more
            </div>
          </div>
        </div>
      </section>

      {/* Image Modal */}
      {showImageModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 animate-modal-fade-in"
          onClick={closeImageModal}
        >
          {/* Animated backdrop */}
          <div className="absolute inset-0 bg-gradient-to-br from-black/95 via-black/90 to-black/95 backdrop-blur-xl transition-all duration-700"></div>
          
          {/* Floating particles in lightbox */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(10)].map((_, i) => (
              <div
                key={i}
                className="absolute rounded-full animate-lightbox-particle"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  width: `${Math.random() * 3 + 1}px`,
                  height: `${Math.random() * 3 + 1}px`,
                  background: `radial-gradient(circle, rgba(244, 114, 182, 0.6) 0%, rgba(244, 114, 182, 0) 70%)`,
                  animationDelay: `${i * 0.2}s`,
                  animationDuration: `${Math.random() * 10 + 5}s`,
                  filter: 'blur(0.5px)'
                }}
              />
            ))}
          </div>

          {/* Modal Content */}
          <div 
            className="relative z-10 max-w-6xl w-full max-h-[90vh] animate-modal-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button with glow */}
            <button 
              className="absolute -top-10 sm:-top-14 right-0 text-white hover:text-pink-300 transition-colors z-10 p-2 sm:p-3 bg-black/40 backdrop-blur-sm rounded-full hover:bg-black/60 hover:scale-110 transition-all duration-500 group"
              onClick={closeImageModal}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-pink-500/0 to-rose-500/0 rounded-full group-hover:from-pink-500/20 group-hover:to-rose-500/20 transition-all duration-500"></div>
              <X className="w-6 h-6 sm:w-8 sm:h-8 relative" />
            </button>
            
            {/* Navigation Buttons with enhanced effects */}
            <button 
              className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-2 sm:-translate-x-4 p-3 sm:p-4 text-white hover:text-pink-300 transition-colors z-10 bg-black/40 backdrop-blur-sm rounded-r-full hover:bg-black/60 hover:scale-110 transition-all duration-500 group"
              onClick={(e) => {
                e.stopPropagation();
                prevImage();
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-pink-500/0 to-rose-500/0 rounded-r-full group-hover:from-pink-500/20 group-hover:to-rose-500/20 transition-all duration-500"></div>
              <ChevronLeft className="w-6 h-6 sm:w-8 sm:h-8 relative" />
            </button>
            
            <button 
              className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-2 sm:translate-x-4 p-3 sm:p-4 text-white hover:text-pink-300 transition-colors z-10 bg-black/40 backdrop-blur-sm rounded-l-full hover:bg-black/60 hover:scale-110 transition-all duration-500 group"
              onClick={(e) => {
                e.stopPropagation();
                nextImage();
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-l from-pink-500/0 to-rose-500/0 rounded-l-full group-hover:from-pink-500/20 group-hover:to-rose-500/20 transition-all duration-500"></div>
              <ChevronRight className="w-6 h-6 sm:w-8 sm:h-8 relative" />
            </button>
            
            {/* Image Container with enhanced border */}
            <div className="relative">
              <div className="absolute -inset-2 sm:-inset-4 bg-gradient-to-r from-pink-500 via-rose-500 to-amber-500 rounded-lg sm:rounded-xl blur-lg opacity-30 animate-gradient-x"></div>
              <img 
                src={destinations[selectedDestination].images[currentImageIndex]}
                alt={destinations[selectedDestination].name}
                className="relative w-full max-h-[60vh] sm:max-h-[70vh] object-contain rounded-lg sm:rounded-xl shadow-2xl border-4 border-transparent bg-gradient-to-r from-pink-500 via-rose-500 to-amber-500 bg-clip-border"
              />
              
              {/* Image Info with gradient background */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/95 via-black/80 to-transparent p-4 sm:p-6 md:p-8 rounded-b-lg sm:rounded-b-xl">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-0">
                  <div>
                    <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-1 sm:mb-2 animate-fade-in">{destinations[selectedDestination].name}</h3>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4 md:gap-6 text-white/80 text-sm sm:text-base">
                      <div className="flex items-center gap-1 sm:gap-2 backdrop-blur-sm bg-white/10 px-2 py-1 rounded-full">
                        <Camera className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span>{destinations[selectedDestination].description}</span>
                      </div>
                      <span className="px-2 sm:px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full">
                        {destinations[selectedDestination].type}
                      </span>
                      <span className="text-pink-200 animate-pulse">
                        Image {currentImageIndex + 1} of {destinations[selectedDestination].images.length}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 sm:gap-3">
                    <button 
                      onClick={() => toggleLike(selectedDestination)}
                      className="p-2 sm:p-3 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-all transform hover:scale-110 group relative overflow-hidden"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-pink-500/0 to-rose-500/0 group-hover:from-pink-500/20 group-hover:to-rose-500/20 transition-all duration-500"></div>
                      <Heart 
                        className={`w-5 h-5 sm:w-6 sm:h-6 relative ${likedDestinations.has(selectedDestination) ? 'fill-rose-500 text-rose-500 scale-110 animate-heart-pop' : 'text-white'}`}
                      />
                    </button>
                    <button className="p-2 sm:p-3 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-all transform hover:scale-110 group relative overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-r from-pink-500/0 to-rose-500/0 group-hover:from-pink-500/20 group-hover:to-rose-500/20 transition-all duration-500"></div>
                      <Share2 className="w-5 h-5 sm:w-6 sm:h-6 text-white relative" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Thumbnail Strip with enhanced effects */}
            <div className="flex justify-center gap-2 sm:gap-3 mt-4 sm:mt-6 overflow-x-auto pb-2 animate-fade-in" style={{ animationDelay: '200ms' }}>
              {destinations[selectedDestination].images.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-lg overflow-hidden transition-all duration-500 relative group ${
                    currentImageIndex === index 
                      ? 'ring-2 sm:ring-4 ring-pink-500 scale-110 shadow-lg' 
                      : 'opacity-70 hover:opacity-100 hover:scale-105'
                  }`}
                >
                  {/* Glow effect for active thumbnail */}
                  {currentImageIndex === index && (
                    <div className="absolute -inset-1 bg-gradient-to-r from-pink-500 to-rose-500 rounded-lg blur-sm opacity-50"></div>
                  )}
                  <img 
                    src={img}
                    alt={`${destinations[selectedDestination].name} ${index + 1}`}
                    className="w-full h-full object-cover relative"
                  />
                  {/* Hover effect */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </button>
              ))}
            </div>
          </div>
          
          {/* Instructions with fade effect */}
          <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 text-white/60 text-xs sm:text-sm text-center px-4 animate-fade-in" style={{ animationDelay: '400ms' }}>
            <div className="flex items-center gap-2 animate-pulse-slow">
              <span>Use arrows or tap thumbnails</span>
              <span className="text-pink-300">•</span>
              <span>Tap outside to close</span>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced CSS Animations */}
      <style jsx>{`
        @keyframes bubble-float {
          0%, 100% {
            transform: translateY(0) translateX(0) scale(1) rotate(0deg);
            opacity: 0.3;
          }
          25% {
            transform: translateY(-40px) translateX(15px) scale(1.15) rotate(5deg);
            opacity: 0.7;
          }
          50% {
            transform: translateY(-70px) translateX(-10px) scale(1) rotate(0deg);
            opacity: 0.4;
          }
          75% {
            transform: translateY(-25px) translateX(20px) scale(1.1) rotate(-5deg);
            opacity: 0.6;
          }
        }
        
        @keyframes star-twinkle {
          0%, 100% {
            opacity: 0.3;
            transform: scale(1) rotate(0deg);
            filter: drop-shadow(0 0 2px rgba(255, 182, 193, 0.6));
          }
          25% {
            opacity: 0.9;
            transform: scale(1.3) rotate(90deg);
            filter: drop-shadow(0 0 5px rgba(255, 182, 193, 0.9));
          }
          50% {
            opacity: 0.4;
            transform: scale(0.95) rotate(180deg);
            filter: drop-shadow(0 0 3px rgba(255, 182, 193, 0.7));
          }
          75% {
            opacity: 0.8;
            transform: scale(1.2) rotate(270deg);
            filter: drop-shadow(0 0 4px rgba(255, 182, 193, 0.8));
          }
        }
        
        @keyframes heart-float {
          0%, 100% {
            transform: translateY(0) scale(1) rotate(0deg);
            opacity: 0.2;
          }
          25% {
            transform: translateY(-50px) scale(1.2) rotate(45deg);
            opacity: 0.6;
          }
          50% {
            transform: translateY(-90px) scale(0.9) rotate(90deg);
            opacity: 0.3;
          }
          75% {
            transform: translateY(-35px) scale(1.1) rotate(135deg);
            opacity: 0.5;
          }
        }
        
        @keyframes orb-float {
          0%, 100% {
            transform: translateY(0) rotate(0deg) scale(1);
          }
          50% {
            transform: translateY(-25px) rotate(-3deg) scale(1.05);
          }
        }
        
        @keyframes orb-float-slow {
          0%, 100% {
            transform: translateY(0) rotate(0deg) scale(1);
          }
          33% {
            transform: translateY(-15px) rotate(2deg) scale(1.03);
          }
          66% {
            transform: translateY(-10px) rotate(-1deg) scale(0.98);
          }
        }
        
        @keyframes wave {
          0%, 100% {
            transform: translateX(0) scaleY(1);
          }
          25% {
            transform: translateX(-25%) scaleY(1.15);
          }
          50% {
            transform: translateX(-50%) scaleY(0.9);
          }
          75% {
            transform: translateX(-75%) scaleY(1.1);
          }
        }
        
        @keyframes gradient-x {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
        
        @keyframes gradient-flow {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
        
        @keyframes width-pulse {
          0%, 100% {
            width: 0;
            left: 50%;
          }
          50% {
            width: 100%;
            left: 0;
          }
        }
        
        @keyframes width-pulse-slow {
          0%, 100% {
            width: 0;
            left: 50%;
          }
          50% {
            width: 60%;
            left: 20%;
          }
        }
        
        @keyframes slide-up-stagger {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fade-in-stagger {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.7;
            transform: scale(1.05);
          }
        }
        
        @keyframes pulse-slow {
          0%, 100% {
            opacity: 0.5;
            transform: scale(1);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.05);
          }
        }
        
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-15px);
          }
        }
        
        @keyframes bounce-right {
          0%, 100% {
            transform: translateX(0);
          }
          50% {
            transform: translateX(4px);
          }
        }
        
        @keyframes button-float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-8px);
          }
        }
        
        @keyframes card-float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        @keyframes stat-float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-5px);
          }
        }
        
        @keyframes heart-pop {
          0%, 100% {
            transform: scale(1);
          }
          25% {
            transform: scale(1.3);
          }
          50% {
            transform: scale(0.9);
          }
          75% {
            transform: scale(1.1);
          }
        }
        
        @keyframes button-pulse {
          0%, 100% {
            box-shadow: 0 10px 30px rgba(244, 114, 182, 0.3);
          }
          50% {
            box-shadow: 0 15px 40px rgba(244, 114, 182, 0.5);
          }
        }
        
        @keyframes sparkle {
          0%, 100% {
            opacity: 0;
            transform: scale(0);
          }
          50% {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes modal-fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes modal-slide-up {
          from {
            opacity: 0;
            transform: translateY(50px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes lightbox-particle {
          0%, 100% {
            transform: translateY(0) translateX(0);
            opacity: 0.2;
          }
          50% {
            transform: translateY(-50px) translateX(20px);
            opacity: 0.8;
          }
        }
        
        /* Animation classes */
        .animate-bubble-float {
          animation: bubble-float 25s ease-in-out infinite;
          will-change: transform, opacity;
        }
        
        .animate-star-twinkle {
          animation: star-twinkle 18s ease-in-out infinite;
          will-change: transform, opacity, filter;
        }
        
        .animate-heart-float {
          animation: heart-float 20s ease-in-out infinite;
          will-change: transform, opacity;
        }
        
        .animate-orb-float {
          animation: orb-float 15s ease-in-out infinite;
          will-change: transform;
        }
        
        .animate-orb-float-slow {
          animation: orb-float-slow 20s ease-in-out infinite;
          will-change: transform;
        }
        
        .animate-wave {
          animation: wave 30s linear infinite;
          will-change: transform;
        }
        
        .animate-gradient-x {
          animation: gradient-x 3s ease infinite;
          background-size: 200% 200%;
          will-change: background-position;
        }
        
        .animate-gradient-flow {
          animation: gradient-flow 3s ease infinite;
          background-size: 200% auto;
          will-change: background-position;
        }
        
        .animate-slide-up-stagger {
          animation: slide-up-stagger 1s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        
        .animate-fade-in-stagger {
          animation: fade-in-stagger 1s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        
        .animate-width-pulse {
          animation: width-pulse 3s ease-in-out infinite;
          will-change: width, left;
        }
        
        .animate-width-pulse-slow {
          animation: width-pulse-slow 4s ease-in-out infinite;
          will-change: width, left;
        }
        
        .animate-pulse {
          animation: pulse 2s ease-in-out infinite;
          will-change: transform, opacity;
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 3s ease-in-out infinite;
          will-change: transform, opacity;
        }
        
        .animate-spin {
          animation: spin 20s linear infinite;
          will-change: transform;
        }
        
        .animate-spin-slow {
          animation: spin-slow 30s linear infinite;
          will-change: transform;
        }
        
        .animate-bounce-slow {
          animation: bounce-slow 3s ease-in-out infinite;
          will-change: transform;
        }
        
        .animate-bounce-right {
          animation: bounce-right 2s ease-in-out infinite;
          will-change: transform;
        }
        
        .animate-button-float {
          animation: button-float 6s ease-in-out infinite;
          will-change: transform;
        }
        
        .animate-card-float {
          animation: card-float 8s ease-in-out infinite;
          will-change: transform;
        }
        
        .animate-stat-float {
          animation: stat-float 7s ease-in-out infinite;
          will-change: transform;
        }
        
        .animate-heart-pop {
          animation: heart-pop 0.5s ease-in-out;
          will-change: transform;
        }
        
        .animate-button-pulse {
          animation: button-pulse 2s ease-in-out infinite;
          will-change: box-shadow;
        }
        
        .animate-sparkle {
          animation: sparkle 1.5s ease-in-out infinite;
          will-change: transform, opacity;
        }
        
        .animate-modal-fade-in {
          animation: modal-fade-in 0.5s ease-out forwards;
        }
        
        .animate-modal-slide-up {
          animation: modal-slide-up 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        
        .animate-lightbox-particle {
          animation: lightbox-particle 15s ease-in-out infinite;
          will-change: transform, opacity;
        }
        
        .line-clamp-2 {
          overflow: hidden;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
        }
      `}</style>
    </>
  );
};

export default Destination;